import { IPageFilters } from '@growdash/growintel/constants/Tabs';
import { Button, Input, Label } from '@growdash/design-system/atoms';
import { ComboBox } from '@growdash/design-system/molecules';
import { cn } from '@growdash/design-system/utils/helpers.js';
import useMetaData from '@growdash/growintel/hooks/useMetaData';
import useTabState from '@growdash/growintel/hooks/useTabState';
import { Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';

type BrandFilters = Partial<IPageFilters['search']['brand']>;

const MemoizedComboBox = React.memo(ComboBox);

export default function BrandFilters() {
  const { filters, updateTabState } = useTabState('search');
  const { brand: brandFilters } = filters;
  const { name, area, city, cuisine } = brandFilters;
  const { areas, cities, cuisines } = useMetaData();

  const [tempName, setTempName] = useState(name);
  const [tempArea, setTempArea] = useState(area);
  const [tempCity, setTempCity] = useState(city);
  const [tempCuisine, setTempCuisine] = useState(cuisine);

  const cityOptions = useMemo(
    () => cities.map((c) => ({ label: c, value: c })),
    [cities],
  );
  const areaOptions = useMemo(
    () => areas.map((a) => ({ label: a, value: a })),
    [areas],
  );
  const cuisineOptions = useMemo(
    () => cuisines.map((c) => ({ label: c, value: c })),
    [cuisines],
  );

  const disabled =
    tempName.length < 3 && !tempCity && !tempArea && !tempCuisine;

  const handleUpdateBrandFilter = () => {
    updateTabState({
      filters: {
        ...filters,
        brand: {
          ...brandFilters,
          ...{
            name: tempName,
            area: tempArea,
            city: tempCity,
            cuisine: tempCuisine,
          },
        },
      },
    });
  };

  const handleClearFilters = () => {
    setTempCity('');
    setTempName('');
    setTempCuisine('');
    setTempArea('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleUpdateBrandFilter();
    }
  };

  return (
    <div className="flex items-end gap-2 pb-px">
      <div className="flex flex-col gap-2">
        <Label>Brand name</Label>
        <div className="relative lg:min-w-44">
          <Input
            onKeyDown={handleKeyDown}
            autoFocus
            onChange={(e) => {
              setTempName(e.target.value);
            }}
            value={tempName}
            placeholder="Brand name"
            className="w-full"
          />
          <div className="absolute right-2 top-[50%] -translate-y-[50%]">
            <Search color="#72778A" size={16} />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label>City</Label>
        <div className="relative">
          <MemoizedComboBox
            placeholder="City"
            options={cityOptions}
            value={tempCity}
            onValueChange={setTempCity}
            className="lg:min-w-44"
            onClear={() => {
              setTempCity(undefined);
            }}
          />
        </div>
      </div>
      {areaOptions.length > 0 && (
        <div className="flex flex-col gap-2">
          <Label>Area</Label>
          <div className="relative">
            <MemoizedComboBox
              placeholder="Area"
              options={areaOptions}
              value={tempArea}
              onValueChange={setTempArea}
              className={cn('lg:min-w-44')}
              onClear={() => {
                setTempArea(undefined);
              }}
            />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Label>Cuisine</Label>
        <div className="relative">
          <MemoizedComboBox
            placeholder="Cuisine"
            options={cuisineOptions}
            value={tempCuisine}
            onValueChange={setTempCuisine}
            className="lg:min-w-44"
            onClear={() => {
              setTempCuisine(undefined);
            }}
          />
        </div>
      </div>
      <div className="flex items-center">
        <Button
          disabled={disabled}
          variant="default"
          onClick={handleUpdateBrandFilter}
        >
          Search
        </Button>
        {!disabled && (
          <Button
            onClick={handleClearFilters}
            variant="link"
            className="animate-fade-in text-secondary underline-offset-[5px] underline"
          >
            Clear Filters
          </Button>
        )}
      </div>
    </div>
  );
}
